import React, { useCallback, useMemo, useState } from 'react';

import { GalleryViewMode } from '../ImageGallery.common';

export type GalleryStateProps = {
  galleryViewMode: GalleryViewMode;
  lightboxIndex: number;
  setLightboxIndex: React.Dispatch<React.SetStateAction<number>>;
  onClose: () => void;
  onImageClick: (index: number) => void;
  onViewGallery?: () => void;
};

export type GalleryState = {
  isOpen: boolean;
  openLightbox: (index: number) => void;
  openGrid: () => void;
  close: () => void;
  galleryProps: GalleryStateProps;
};

type Options = {
  initialViewMode?: GalleryViewMode;
  hasGridView?: boolean;
};

const useGalleryState = ({
  initialViewMode = GalleryViewMode.Lightbox,
  hasGridView = true,
}: Options = {}): GalleryState => {
  const [isOpen, setIsOpen] = useState(false);
  const [galleryViewMode, setGalleryViewMode] = useState(initialViewMode);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const showLightboxAt = useCallback((index: number): void => {
    setLightboxIndex(index);
    setGalleryViewMode(GalleryViewMode.Lightbox);
  }, []);

  const openLightbox = useCallback(
    (index: number): void => {
      showLightboxAt(index);
      setIsOpen(true);
    },
    [showLightboxAt]
  );

  const openGrid = useCallback((): void => {
    setGalleryViewMode(GalleryViewMode.Grid);
    setIsOpen(true);
  }, []);

  const close = useCallback((): void => setIsOpen(false), []);

  const showGrid = useCallback((): void => setGalleryViewMode(GalleryViewMode.Grid), []);

  const galleryProps = useMemo(
    () => ({
      galleryViewMode,
      lightboxIndex,
      setLightboxIndex,
      onClose: close,
      onImageClick: showLightboxAt,
      onViewGallery: hasGridView ? showGrid : undefined,
    }),
    [galleryViewMode, lightboxIndex, close, showLightboxAt, showGrid, hasGridView]
  );

  return { isOpen, openLightbox, openGrid, close, galleryProps };
};

export default useGalleryState;
